<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcEmployeeCard',
    inheritAttrs: false,
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    item: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const ux = ref([]);

  const $data = computed(() => ({
    ux: ux.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'ux' }],
    loaders: {},
    ux: {},
    dataRefs: { ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.item,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('item');
    }
  );

  const actions = {};

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpBox direction="column">
    <ItpText strong :text="item.name" spacing="mb-2"> </ItpText>
    <ItpText spacing="mb-2" :text="item.job_title" v-if="item.job_title">
    </ItpText>
    <ItpBox spacing="mb-2" alignItems="center" v-if="item.phone">
      <Icon
        icon="ic:baseline-phone"
        :title="pgettext('Contact', 'Phone Number')"
        class="me-2"
      >
      </Icon>
      <ItpText :text="item.phone"> </ItpText>
    </ItpBox>
    <ItpBox spacing="mb-2" alignItems="center" v-if="item.mobile">
      <Icon
        icon="mdi:cellphone-basic"
        :title="pgettext('Contact', 'Mobile')"
        class="me-2"
      >
      </Icon>
      <ItpText :text="item.mobile"> </ItpText>
    </ItpBox>
    <ItpBox spacing="mb-2" alignItems="center" v-if="item.fax">
      <Icon icon="mdi:fax" :title="pgettext('Contact', 'Fax')" class="me-2">
      </Icon>
      <ItpText :text="item.fax"> </ItpText>
    </ItpBox>
    <ItpBox spacing="mb-2" alignItems="center" v-if="item.email">
      <Icon icon="mdi:at" :title="pgettext('Contact', 'Email')" class="me-2">
      </Icon>
      <ItpText :text="item.email"> </ItpText>
    </ItpBox>
    <ItpBox v-if="item.notes">
      <ItpText :text="item.notes" pre> </ItpText>
    </ItpBox>
  </ItpBox>
</template>
